import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Monitor, 
  Plus, 
  Trash2, 
  RefreshCw, 
  Copy, 
  ToggleLeft, 
  ToggleRight,
  Search,
  X,
  Loader2,
  CheckCircle,
  Clock,
  CreditCard,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const DevicesPage = () => {
  const { api, provider } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newDevice, setNewDevice] = useState({ name: '', notes: '' });
  const [creating, setCreating] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(null);

  const fetchDevices = async () => {
    try {
      const response = await api.get('/devices');
      setDevices(response.data);
    } catch (error) {
      toast.error('Failed to fetch devices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const handleCreateDevice = async (e) => {
    e.preventDefault();
    if (!newDevice.name.trim()) {
      toast.error('Device name is required');
      return;
    }
    
    // Check if user needs to pay
    const needsPayment = provider?.free_device_used && devices.length >= 1;
    
    setCreating(true);
    try {
      const response = await api.post('/devices', newDevice);
      setDevices(prev => [response.data, ...prev]);
      setShowModal(false);
      setNewDevice({ name: '', notes: '' });
      
      if (response.data.is_trial) {
        toast.success('Free trial device created! Valid for 7 days.');
      } else {
        toast.success('Device created successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create device');
    } finally {
      setCreating(false);
    }
  };

  const handleRegenerateCode = async (deviceId) => {
    try {
      const response = await api.put(`/devices/${deviceId}/regenerate-code`);
      setDevices(prev => prev.map(d => d.id === deviceId ? { ...d, access_code: response.data.access_code } : d));
      toast.success('Access code regenerated');
    } catch (error) {
      toast.error('Failed to regenerate code');
    }
  };

  const handleToggleStatus = async (deviceId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/devices/${deviceId}/status?status=${newStatus}`);
      setDevices(prev => prev.map(d => d.id === deviceId ? { ...d, status: newStatus } : d));
      toast.success(`Device ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to delete this device?')) return;
    
    try {
      await api.delete(`/devices/${deviceId}`);
      setDevices(prev => prev.filter(d => d.id !== deviceId));
      toast.success('Device deleted');
    } catch (error) {
      toast.error('Failed to delete device');
    }
  };

  const handleRenewSubscription = async (deviceId) => {
    setProcessingPayment(deviceId);
    try {
      const response = await api.post('/payments/checkout', {
        device_id: deviceId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to start checkout');
      setProcessingPayment(null);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success(`${label} copied to clipboard`);
      } catch (e) {
        toast.error('Failed to copy');
      }
      document.body.removeChild(textArea);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return true;
    return new Date(dateStr) < new Date();
  };

  const daysUntilExpiry = (dateStr) => {
    if (!dateStr) return 0;
    const diff = new Date(dateStr) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const filteredDevices = devices.filter(device =>
    device.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.mac_address.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.access_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const canAddFreeDevice = !provider?.free_device_used;

  return (
    <div data-testid="devices-page" className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Device Management</h1>
          <p className="text-muted-foreground">Manage MAC addresses and access codes</p>
        </div>
        <button
          data-testid="add-device-modal-button"
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Device
          {canAddFreeDevice && <span className="text-xs bg-green-500/20 px-2 py-0.5 rounded">FREE</span>}
        </button>
      </div>

      {/* Pricing Info Banner */}
      {!canAddFreeDevice && devices.length > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-blue-400" />
            <div>
              <p className="text-sm text-blue-200">
                Additional devices: <span className="font-semibold">£3.99/year</span> per device
              </p>
              <p className="text-xs text-blue-400">Subscriptions renew annually. Cancel anytime.</p>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input
          data-testid="search-devices-input"
          type="text"
          placeholder="Search by name, MAC, or code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full bg-background border border-border rounded-lg py-3 pl-11 pr-4 text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary transition-all"
        />
      </div>

      {/* Devices Grid */}
      {filteredDevices.length === 0 ? (
        <div className="empty-state dashboard-card">
          <Monitor className="w-16 h-16 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            {searchQuery ? 'No devices found' : 'No devices yet'}
          </h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery 
              ? 'Try adjusting your search query' 
              : 'Create your first device for FREE (7-day trial)'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Add Free Device
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredDevices.map((device) => {
            const expired = isExpired(device.subscription_expires);
            const daysLeft = daysUntilExpiry(device.subscription_expires);
            const expiringSoon = daysLeft <= 7 && daysLeft > 0;
            
            return (
              <div key={device.id} className={`device-card ${expired ? 'border-red-500/30' : ''}`} data-testid={`device-card-${device.id}`}>
                {/* Device Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-foreground text-lg">{device.name}</h3>
                    {device.notes && (
                      <p className="text-sm text-muted-foreground">{device.notes}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      device.status === 'active' ? 'status-active' : 'status-inactive'
                    }`}>
                      {device.status}
                    </span>
                    {device.is_trial && (
                      <span className="px-2 py-0.5 rounded text-xs bg-yellow-500/20 text-yellow-400">
                        Trial
                      </span>
                    )}
                  </div>
                </div>

                {/* Subscription Status */}
                <div className={`rounded-lg p-3 mb-4 ${
                  expired 
                    ? 'bg-red-500/10 border border-red-500/20' 
                    : expiringSoon 
                      ? 'bg-yellow-500/10 border border-yellow-500/20'
                      : 'bg-green-500/10 border border-green-500/20'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {expired ? (
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                      ) : (
                        <Clock className="w-4 h-4 text-green-400" />
                      )}
                      <span className={`text-sm font-medium ${
                        expired ? 'text-red-400' : expiringSoon ? 'text-yellow-400' : 'text-green-400'
                      }`}>
                        {expired 
                          ? 'Subscription Expired' 
                          : expiringSoon 
                            ? `Expires in ${daysLeft} days`
                            : `Valid until ${formatDate(device.subscription_expires)}`
                        }
                      </span>
                    </div>
                    {(expired || expiringSoon) && (
                      <button
                        onClick={() => handleRenewSubscription(device.id)}
                        disabled={processingPayment === device.id}
                        className="px-3 py-1 bg-primary text-primary-foreground rounded text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
                      >
                        {processingPayment === device.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          'Renew £3.99'
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Access Code */}
                <div className="code-box mb-4">
                  <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Access Code</p>
                  <div className="code">{device.access_code}</div>
                </div>

                {/* MAC Address */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">MAC Address</p>
                    <div className="mac-box">
                      <span className="mac">{device.mac_address}</span>
                      <button
                        data-testid={`copy-mac-${device.id}`}
                        onClick={() => copyToClipboard(device.mac_address, 'MAC address')}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <button
                    data-testid={`copy-code-${device.id}`}
                    onClick={() => copyToClipboard(device.access_code, 'Access code')}
                    className="px-3 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 text-sm flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Copy Code
                  </button>
                </div>

                {/* IPTV Subscription Info */}
                {device.iptv_subscription && (
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 mb-4">
                    <p className="text-xs text-purple-400 uppercase tracking-wider mb-1">IPTV Package</p>
                    <p className="text-sm text-purple-200 font-medium">
                      {device.iptv_subscription.package_name}
                    </p>
                    <p className="text-xs text-purple-400">
                      {device.iptv_subscription.provider_name} • 
                      Expires: {formatDate(device.iptv_subscription.end_date)}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-4 border-t border-border">
                  <button
                    data-testid={`regenerate-code-${device.id}`}
                    onClick={() => handleRegenerateCode(device.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 text-sm transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    New Code
                  </button>
                  <button
                    data-testid={`toggle-status-${device.id}`}
                    onClick={() => handleToggleStatus(device.id, device.status)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 text-sm transition-colors"
                  >
                    {device.status === 'active' ? (
                      <>
                        <ToggleRight className="w-4 h-4" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="w-4 h-4" />
                        Activate
                      </>
                    )}
                  </button>
                  <button
                    data-testid={`delete-device-${device.id}`}
                    onClick={() => handleDeleteDevice(device.id)}
                    className="px-3 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Last Connected */}
                {device.last_connected && (
                  <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-green-400" />
                    Last connected: {new Date(device.last_connected).toLocaleString()}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Device Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md mx-4 animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">Create New Device</h2>
              <button
                data-testid="close-modal-button"
                onClick={() => setShowModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Pricing Info */}
            <div className={`rounded-lg p-3 mb-4 ${
              canAddFreeDevice 
                ? 'bg-green-500/10 border border-green-500/20' 
                : 'bg-blue-500/10 border border-blue-500/20'
            }`}>
              {canAddFreeDevice ? (
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-200">
                    This device is <span className="font-bold">FREE</span> (7-day trial)
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-400" />
                  <span className="text-sm text-blue-200">
                    This device costs <span className="font-bold">£3.99/year</span>
                  </span>
                </div>
              )}
            </div>

            <form onSubmit={handleCreateDevice}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Device Name *
                  </label>
                  <input
                    data-testid="device-name-input"
                    type="text"
                    value={newDevice.name}
                    onChange={(e) => setNewDevice(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Living Room TV"
                    className="w-full bg-background border border-border rounded-lg py-3 px-4 text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    data-testid="device-notes-input"
                    value={newDevice.notes}
                    onChange={(e) => setNewDevice(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional notes..."
                    rows={3}
                    className="w-full bg-background border border-border rounded-lg py-3 px-4 text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary transition-all resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="create-device-submit"
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      {canAddFreeDevice ? 'Create Free Device' : 'Create Device'}
                    </>
                  )}
                </button>
              </div>
            </form>

            <p className="text-xs text-muted-foreground text-center mt-4">
              A unique MAC address and 4-digit code will be generated automatically
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevicesPage;
